import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Box, Loader2, Rotate3d } from "lucide-react";
import STLViewer from "@/components/STLViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function STLPreviewModal({ quote, token, onClose }) {
  const [buffer, setBuffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!quote) return;
    setLoading(true);
    setError(false);
    setBuffer(null);
    axios
      .get(`${API}/quotes/${quote.id}/file`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "arraybuffer",
      })
      .then((res) => {
        if (active) {
          setBuffer(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [quote, token]);

  const isStl = quote?.file_name?.toLowerCase().endsWith(".stl");

  return (
    <AnimatePresence>
      {quote && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          onClick={onClose}
          data-testid="stl-preview-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 20 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl bg-[#111827] border border-white/10"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center bg-[#00C8D7]/10 text-[#00C8D7] border border-[#00C8D7]/30">
                  <Box size={18} />
                </span>
                <div>
                  <h2 className="font-display font-bold text-lg tracking-tight">Anteprima 3D</h2>
                  <span className="font-mono-tech text-xs text-slate-500">{quote.file_name}</span>
                </div>
              </div>
              <button data-testid="stl-preview-close" onClick={onClose} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="relative h-[420px] bg-[#0A0E14] tech-grid-bg" data-testid="stl-preview-body">
              {loading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-slate-400">
                  <Loader2 size={28} className="animate-spin text-[#00C8D7]" />
                  <span className="font-mono-tech text-xs">Caricamento modello...</span>
                </div>
              )}
              {!loading && (error || !isStl) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-slate-400 px-6 text-center">
                  <Box size={32} className="text-slate-600" />
                  <span className="text-sm">
                    {isStl
                      ? "Impossibile caricare l'anteprima di questo file."
                      : "Anteprima disponibile solo per i file .stl. Puoi comunque scaricare il file."}
                  </span>
                </div>
              )}
              {!loading && !error && isStl && buffer && <STLViewer arrayBuffer={buffer} />}
            </div>

            <div className="px-6 py-3 border-t border-white/10 flex items-center gap-2 text-slate-500">
              <Rotate3d size={15} />
              <span className="font-mono-tech text-xs">Trascina per ruotare · scorri per zoomare</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
